import {
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import CustomSelect from '../../components/CustomSelect';
import CustomInput from '../../components/CustomInput';
import {
  getBankBranchData,
  getBankDropdownData,
  getLoanTypeData,
  getMasterListType,
  saveApiData,
  setBranchDetails,
  getStateData,
  getCityData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { RevenueApi } from '../../utils/Apis';

function AddEditRevenue() {
  const [userType, setUserType] = useState('Manual');
  const [showHide, setShowHide] = useState('Manual');

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rowId } = useParams();

  const { LoanTypeDrop, BankDrop, MasterTypeListDrop, BankBranchDrop, stateDrop, CityDrop } =
    useGlobalSelector();
  const { LoginData } = useLoginSelector();

  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    watch,
    trigger, // Add this
    formState: { errors }
  } = useForm({
    mode: 'onBlur', // Add this to validate on blur
    defaultValues: {
      bankId: '',
      fileNumber: '',
      borrowerName: '',
      branchId: '',
      loanTypeId: '',
      misMonth: '',
      provinceId: '',
      districtId: ''
    }
  });

  const watchFileTypeId = watch('revenueFileTypeId');
  const fileTypeInputs = {
    128: { name: 'fileRequestDate', label: 'Request Date', type: 'date' },
    130: { name: 'fileApprovalDate', label: 'Approval Date', type: 'date' }, // fixed type from 'fileApprovalDate' to 'date'
    132: { name: 'fileDisbursedDate', label: 'Disburse Date', type: 'date' }
  };
  const inputProps = fileTypeInputs[watchFileTypeId];

  const loanAmountInput = {
    132: { name: 'loanAmountDisbursed', label: 'Disbursed Amount' },
    default: { name: 'loanAmountRequested', label: 'Total Loan Amount' }
  };
  const inputAmount = loanAmountInput[watchFileTypeId] || loanAmountInput.default;

  const handleShowHide = (event) => {
    const getUserType = event.target.value;
    setShowHide(getUserType);
  };

  const handleBranchDetails = (bankValue) => {
    if (!bankValue) {
      setValue('branchId', null);
      dispatch(setBranchDetails(null));
      return '';
    }
    dispatch(getBankBranchData(bankValue));
  };

  // State → District cascading
  const handleStateChange = (stateVal) => {
    dispatch(setCityData([]));
    setValue('districtId', null);
    if (stateVal) dispatch(getCityData({ stateId: stateVal }));
  };

  // loan type & Bank Dropdown
  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData]);

  // Master Type List
  const getMasterTypeListApi = () => {
    const type = 'REVENUE_FILE_STATUS_TYPE';
    dispatch(getMasterListType(type));
  };

  // Load states on mount
  const getStateDropApi = useCallback(() => {
    dispatch(getStateData({ countryId: 101 }));
  }, [dispatch]);

  const formatToMidnight = (value) => {
    if (!value) return null;
    try {
      return format(new Date(value), "yyyy-MM-dd'T00:00:00'");
    } catch {
      return value;
    }
  };

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        leadId: null,
        empClass: 'Others',
        misIsMatch: false,

        // 🟢 Format all date fields here
        fileRequestDate: formatToMidnight(data.fileRequestDate),
        fileApprovalDate: formatToMidnight(data.fileApprovalDate),
        fileDisbursedDate: formatToMidnight(data.fileDisbursedDate),

        // 🟢 MIS Month: convert "2025-12" → "2025-12-01T00:00:00"
        misMonth: data.misMonth
          ? format(new Date(`${data.misMonth}-01`), "yyyy-MM-dd'T00:00:00'")
          : null
      };
      try {
        const res = await dispatch(saveApiData(RevenueApi?.addNewFile, finalData));
        if (res?.status === 'success') navigate('/revenue');
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData]
  );

  useEffect(() => {
    getMasterTypeListApi();
    getLoanTypeBankDropAPi();
    getStateDropApi();
  }, []);

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info={rowId ? 'Edit File' : 'Add New File'} />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            File Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <FormControl sx={{ width: '100%', marginBottom: '1rem' }}>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="Manual"
                name="radio-buttons-group"
                value={userType}
                onChange={(e) => {
                  handleShowHide(e);
                  setUserType(e.target.value);
                }}
              >
                <Box className="displayFlex alignItemCenter">
                  <FormControlLabel
                    value="Manual"
                    control={<Radio size="small" />}
                    sx={{ marginRight: '1rem' }}
                    label="Manual"
                  />
                  {/* <FormControlLabel
                    value="Fetch"
                    control={<Radio size="small" />}
                    sx={{ marginRight: '1rem' }}
                    label="Fetch"
                  /> */}
                </Box>
              </RadioGroup>
            </FormControl>
            {showHide === 'Manual' && (
              <>
                <form id="addRevenue" onSubmit={handleReduxSubmit(onSubmit)}>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={4}>
                      <CustomSelect
                        name="bankId"
                        label="Bank Name"
                        control={control}
                        errors={errors}
                        options={BankDrop || []}
                        isRequired
                        onChange={(event, val, option, reason, details) => {
                          handleBranchDetails(val);
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomInput
                        name="fileNumber"
                        label="File No"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="FILE_NO"
                        fieldType="TEXT_FIELD"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomInput
                        name="borrowerName"
                        label="Borrower Name"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="NAME"
                        fieldType="ALPHABET"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomSelect
                        name="branchId"
                        label="Branch Name"
                        control={control}
                        errors={errors}
                        options={BankBranchDrop || []}
                        isRequired
                        fieldCode="NAME"
                        fieldType="ALPHABET"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomSelect
                        name="revenueFileTypeId"
                        label="File Type"
                        control={control}
                        errors={errors}
                        options={MasterTypeListDrop || []}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomInput
                        name={inputAmount?.name}
                        label={inputAmount?.label}
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="AMOUNT"
                        fieldType="AMOUNT_FIELD" // Numbers with decimals and commas
                        type="text"
                      />
                    </Grid>
                    {inputProps && (
                      <Grid item xs={4}>
                        <CustomInput
                          name={inputProps.name}
                          type={inputProps.type}
                          label={inputProps.label}
                          control={control}
                          errors={errors}
                          isRequired
                          fieldCode="DATE"
                          fieldType="DATE_FIELD"
                        />
                      </Grid>
                    )}
                    <Grid item xs={4}>
                      <CustomSelect
                        name="loanTypeId"
                        label="Loan Product Type"
                        control={control}
                        errors={errors}
                        options={LoanTypeDrop || []}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomInput
                        name="misMonth"
                        label="MIS Month"
                        type="month"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="MONTH"
                        fieldType="DATE_FIELD"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomSelect
                        name="provinceId"
                        label="State"
                        control={control}
                        errors={errors}
                        options={stateDrop || []}
                        isRequired
                        onChange={(e, val) => handleStateChange(val)}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <CustomSelect
                        name="districtId"
                        label="District / City"
                        control={control}
                        errors={errors}
                        options={CityDrop || []}
                        isRequired
                      />
                    </Grid>
                  </Grid>{' '}
                </form>{' '}
              </>
            )}
          </Box>
          <Box className="" textAlign="right">
            <Button type="submit" form="addRevenue" variant="contained" className="mainBtn">
              Submit
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
export default AddEditRevenue;

// Top Cities
const topCities = [{ label: 'Mumbai' }, { label: 'Delhi' }, { label: 'Bengaluru' }];

// Top Banks
const topBanks = [{ label: 'Axis Bank' }, { label: 'HDFC Bank' }];
